import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { AppContext } from 'src/components/app-context';
import { useSelector } from 'react-redux';
import { SecondarySearchField } from 'src/components/secondary-search-field';
import { useGlobalPropertySearch } from './global-property-search';
import type { AggregationSection } from '../../../../types';
import { SIDEBAR_STATE } from '../../../sidebar/constants';
import { FILTER_PORTAL_ID } from '../../constants';
import * as S from './styles';
import type { ProductCollectionNonNameSearchFilter } from './types';
import { AllFilterSections } from './all-filter-sections';

type Props = {
  sections: AggregationSection[];
  filters: ProductCollectionNonNameSearchFilter[];
};

const SidebarFilterSection = ({ ...props }: Props) => {
  const { t } = React.useContext(AppContext);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>();
  const sidebarTab = useSelector((state: any) => state.areas.sidebar.tab);
  const {
    isSearching,
    filterSearchQuery,
    filterByPropertySearch,
    hasSectionMatchingFilter,
    onSecondarySearchFieldChange,
  } = useGlobalPropertySearch();
  /**
   * The whole filter slice is rendered through a portal in order to keep it
   * within the route data structure while still being able to show it in the
   * sidebar. We have to do this little dance with local state and an effect
   * to make sure the portal target has been rendered before trying to render
   * into it.
   */
  useEffect(() => {
    if (sidebarTab === SIDEBAR_STATE.FILTER) {
      setTimeout(() => {
        setPortalTarget(document.getElementById(FILTER_PORTAL_ID));
      }, 0);
    }
  }, [sidebarTab]);

  if (!portalTarget) {
    return null;
  }

  return ReactDOM.createPortal(
    <S.Filter>
      <S.FilterTitle>{t('filter', 'filter.title')}</S.FilterTitle>
      <S.FilterSearchWrapper>
        <SecondarySearchField
          value={filterSearchQuery}
          onChange={onSecondarySearchFieldChange}
          clearText={t('common', 'button.clear')}
          placeholder={t('filter', 'properties.search-placeholder')}
        />
      </S.FilterSearchWrapper>
      <AllFilterSections
        {...props}
        isSearching={isSearching}
        filterByPropertySearch={filterByPropertySearch}
        hasSectionMatchingFilter={hasSectionMatchingFilter}
      />
    </S.Filter>,
    portalTarget
  );
};

export default SidebarFilterSection;
